import Tooltip from "@mui/material/Tooltip";
import { Info } from "lucide-react";
import React, { useState } from "react";
import { hazardEvent } from "../../../data/HazardAndVulnerablity";
import { useHazardousVulnerabilityStore } from "../../../store/store";
import { toast } from "react-toastify";

interface HazardData {
  [key: string]: number | string;
}
interface CreateHazardAndVulnerabilityProps {
  onClose: () => void;
}

const CreateHazardAndVulnerability: React.FC<
  CreateHazardAndVulnerabilityProps
> = ({ onClose }) => {
  const hazards = [
    "earthquake",
    "massCasualtyIncident",
    "extremeTemperatures",
    "floodExternal",
    "fireExternal",
    "bombBlast",
    "roadTrafficAccident",
    "epidemicBioTerrorism",
  ];

  // Initialize state with default values for each hazard and event
  const [hazardData, setHazardData] = useState<Record<string, HazardData>>(
    () => {
      const initialData: Record<string, HazardData> = {};
      hazards.forEach((hazard) => {
        initialData[hazard] = {
          probability: 0,
          humanImpact: 0,
          propertyImpact: 0,
          businessImpact: 0,
          preparedness: 0,
          internalResponse: 0,
          externalResponse: 0,
        };
      });
      // Set specific initial values based on existing hardcoded values
      initialData.massCasualtyIncident = {
        probability: 0,
        humanImpact: 0,
        propertyImpact: 0,
        businessImpact: 0,
        preparedness: 0,
        internalResponse: 0,
        externalResponse: 0,
      };
      initialData.extremeTemperatures = {
        probability: 0,
        humanImpact: 0,
        propertyImpact: 0,
        businessImpact: 0,
        preparedness: 0,
        internalResponse: 0,
        externalResponse: 0,
      };
      initialData.floodExternal = {
        probability: 0,
        humanImpact: 0,
        propertyImpact: 0,
        businessImpact: 0,
        preparedness: 0,
        internalResponse: 0,
        externalResponse: 0,
      };
      initialData.fireExternal = {
        probability: 0,
        humanImpact: 0,
        propertyImpact: 0,
        businessImpact: 0,
        preparedness: 0,
        internalResponse: 0,
        externalResponse: 0,
      };
      initialData.bombBlast = {
        probability: 0,
        humanImpact: 0,
        propertyImpact: 0,
        businessImpact: 0,
        preparedness: 0,
        internalResponse: 0,
        externalResponse: 0,
      };
      initialData.roadTrafficAccident = {
        probability: 0,
        humanImpact: 0,
        propertyImpact: 0,
        businessImpact: 0,
        preparedness: 0,
        internalResponse: 0,
        externalResponse: 0,
      };
      initialData.epidemicBioTerrorism = {
        probability: 0,
        humanImpact: 0,
        propertyImpact: 0,
        businessImpact: 0,
        preparedness: 0,
        internalResponse: 0,
        externalResponse: 0,
      };
      return initialData;
    }
  );
  const { createHazardousVulnerability } = useHazardousVulnerabilityStore();
  const handleCreateHazardousVulnerability = async () => {
    try {
      await createHazardousVulnerability(hazardData);
      onClose();
    } catch (error) {
      toast.error("Failed to create hazardous vulnerability");
    }
  };

  // Map event titles to data keys
  const eventKeys = [
    "probability",
    "humanImpact",
    "propertyImpact",
    "businessImpact",
    "preparedness",
    "internalResponse",
    "externalResponse",
  ];

  // Calculate relative threat percentage for a hazard
  const calculateRelativeThreat = (hazard: string): string => {
    const data = hazardData[hazard];
    if (!data) return "0%";

    const values = [
      data.probability,
      data.humanImpact,
      data.propertyImpact,
      data.businessImpact,
      data.preparedness,
      data.internalResponse,
      data.externalResponse,
    ].filter((v) => typeof v === "number" && v > 0) as number[];

    if (values.length === 0) return "0%";

    const sum = values.reduce((acc, val) => acc + val, 0);
    const maxPossible = values.length * 3; // Maximum possible score
    const percentage = Math.round((sum / maxPossible) * 100);
    return `${percentage}%`;
  };

  // Calculate average score for each event column
  const calculateAverageScore = (eventIndex: number): number => {
    const eventKey = eventKeys[eventIndex];
    if (!eventKey) return 0;

    const values = hazards.map((hazard) => {
      const value = hazardData[hazard]?.[eventKey];
      return typeof value === "number" ? value : 0;
    });

    if (values.length === 0) return 0;
    const sum = values.reduce((acc, val) => acc + val, 0);
    return parseFloat((sum / values.length).toFixed(2));
  };

  // Calculate average relative threat
  const calculateAverageRelativeThreat = (): string => {
    const percentages = hazards.map((hazard) => {
      const threat = calculateRelativeThreat(hazard);
      return parseFloat(threat.replace("%", ""));
    });
    const avg =
      percentages.reduce((acc, val) => acc + val, 0) / percentages.length;
    return `${Math.round(avg)}%`;
  };

  // Handle input change
  const handleInputChange = (
    hazard: string,
    eventKey: string,
    value: string
  ) => {
    const numValue = value === "" ? 0 : parseInt(value, 10);
    if (isNaN(numValue) || numValue < 0 || numValue > 3) return;

    setHazardData((prev) => ({
      ...prev,
      [hazard]: {
        ...prev[hazard],
        [eventKey]: numValue,
      },
    }));
  };

  return (
    <>
      <div className="p-4 mt-4 w-full h-full ">
        <div className="bg-white rounded-lg shadow px-6 py-2 ">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-semibold text-gray-900 mb">
              Hazard and Vulnerability Assessment
            </h1>
          </div>

          <div className="max-w-7xl mx-auto overflow-x-auto rounded-lg">
            <div className="bg-purple-800 text-white text-center py-1 ">
              <h1 className="text-lg font-bold text-white">
                HAZARD AND VULNERABILITY ASSESSMENT TOOL
              </h1>
            </div>

            <table className="w-full bg-white border-collapse text-xs">
              <thead>
                <tr className="bg-gray-200">
                  <th className="border border-gray-400 p-2 font-bold">
                    EVENT
                  </th>
                  {hazardEvent.map((event, index) => (
                    <th
                      key={index}
                      className="border border-gray-400 p-2 bg-gray-300 font-bold"
                    >
                      {event.title}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                <tr className="text-xs">
                  <td className="border border-gray-400 p-2 bg-gray-100">
                    Likelihood this will occur
                  </td>
                  <td className="border border-gray-400 p-2 bg-yellow-100 ">
                    <Tooltip
                      title={
                        <div className="flex flex-col">
                          <span>0 = N/A</span>
                          <span>1 = Low</span>
                          <span>2 = Moderate</span>
                          <span>3 = High</span>
                        </div>
                      }
                      placement="top"
                    >
                      <Info className="ml-4 w-5 h-5 inline-block text-gray-500" />
                    </Tooltip>
                  </td>
                  <td className="border border-gray-400 p-2 bg-yellow-100">
                    Possibility of injury
                  </td>
                  <td className="border border-gray-400 p-2 bg-yellow-100">
                    Physical losses and damages
                  </td>
                  <td className="border border-gray-400 p-2 bg-yellow-100">
                    Interruption of services
                  </td>
                  <td className="border border-gray-400 p-2 bg-yellow-100">
                    Preplanning
                  </td>
                  <td className="border border-gray-400 p-2 bg-yellow-100">
                    Time, effectiveness, resources
                  </td>
                  <td className="border border-gray-400 p-2 bg-yellow-100">
                    Community/ Mutual Aid staff and supplies
                  </td>
                  <td className="border border-gray-400 p-2 bg-gray-100 italic">
                    Relative threat*
                  </td>
                </tr>

                {hazards.map((hazard, rowIndex) => {
                  const hazardLabels = [
                    "Earthquake",
                    "Mass Casualty Incident",
                    "Extreme Temperatures",
                    "Flood, External",
                    "Fire, External",
                    "Bomb Blast",
                    "Road Traffic Accident",
                    "Epidemic / Bio terrorism",
                  ];
                  const isEvenRow = rowIndex % 2 === 0;

                  return (
                    <tr
                      key={hazard}
                      className={isEvenRow ? "bg-white" : "bg-gray-100"}
                    >
                      <td className="border border-gray-400 p-2 font-semibold">
                        {hazardLabels[rowIndex]}
                      </td>
                      {eventKeys.map((eventKey, eventIndex) => {
                        const value = hazardData[hazard]?.[eventKey] || 0;
                        return (
                          <td
                            key={eventIndex}
                            className="border border-gray-400  text-center"
                          >
                            <input
                              type="number"
                              min="0"
                              max="3"
                              value={value}
                              onChange={(e) =>
                                handleInputChange(
                                  hazard,
                                  eventKey,
                                  e.target.value
                                )
                              }
                              className="w-full h-full text-center border-none outline-none bg-transparent focus:bg-blue-50 focus:ring-1 focus:ring-blue-300 "
                              style={{ height: "30px", width: "100px" }}
                            />
                          </td>
                        );
                      })}
                      <td className="border border-gray-400 p-2 text-center font-bold">
                        {calculateRelativeThreat(hazard)}
                      </td>
                    </tr>
                  );
                })}
                <tr className="bg-gray-800 text-white font-bold">
                  <td className="border border-gray-400 p-2">AVERAGE SCORE</td>
                  {eventKeys.map((_, eventIndex) => (
                    <td
                      key={eventIndex}
                      className="border border-gray-400 p-2 text-center"
                    >
                      {calculateAverageScore(eventIndex)}
                    </td>
                  ))}
                  <td className="border border-gray-400 p-2 text-center">
                    {calculateAverageRelativeThreat()}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="w-full h-full flex content-end justify-end">
            <div className="flex gap-4 mt-1">
              <button
                onClick={() => {
                  handleCreateHazardousVulnerability();
                }}
                className="btn-primary roundedBt iconWithBt text-uppercase"
              >
                Create
              </button>
              <button
                onClick={onClose}
                className="btn-secondary roundedBt iconWithBt text-uppercase"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateHazardAndVulnerability;
